import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { useSelector } from "react-redux";
import styles from "../../../../../src/pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import CustomDropdown from "../../../../components/CustomDropdown/CustomDropdown";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { buildLessonPlanColumns } from "./lessonPlanColumns";

// CustomDropdown's onChange can emit EITHER the raw value OR the full option
// object ({ label, value }). Always normalize to the primitive value.
const extractValue = (val) =>
  val && typeof val === "object" && "value" in val ? val.value : val;

const buildSubjectOptions = (rawList) => {
  const withSubject = (rawList || []).filter(
    (item) => item?.subject_name && String(item.subject_name).trim() !== "",
  );

  const seen = new Set();
  const options = [];

  withSubject.forEach((item) => {
    const className = item.class_name != null ? String(item.class_name) : "";
    const sectionId = item.section_id != null ? String(item.section_id) : "";
    const subjectName =
      item.subject_name != null ? String(item.subject_name) : "";

    const key = `${className}__${sectionId}__${subjectName}`;
    if (seen.has(key)) return;
    seen.add(key);

    options.push({
      label: [className, sectionId, subjectName].filter(Boolean).join(" - "),
      value: key,
      class_id: item.class_id ?? "",
      class_name: className,
      section_id: sectionId,
      subject_id: item.subject_id ?? "",
      subject_name: subjectName,
    });
  });

  return options;
};

function PreviousYearsLessonPlan() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};
  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const schoolId =
    clientData.school_id != null && clientData.school_id !== ""
      ? Number(clientData.school_id)
      : null;
  const staffId = user?.id != null && user?.id !== "" ? Number(user?.id) : null;
  const staff_Id = clientData?.master_id || "";

  // ─── Academic year dropdown ───
  const [academicOptions, setAcademicOptions] = useState([]);
  const [academicLoading, setAcademicLoading] = useState(false);
  const [selectedAcademicId, setSelectedAcademicId] = useState("");

  const selectedAcademicIdRef = useRef("");
  useEffect(() => {
    selectedAcademicIdRef.current = extractValue(selectedAcademicId);
  }, [selectedAcademicId]);

  // ─── Class / Section / Subject dropdown ───
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [subjectLoading, setSubjectLoading] = useState(false);
  const [selectedSubjectValue, setSelectedSubjectValue] = useState("");
  const subjectInitRef = useRef(false);

  const selectedSubject = useMemo(
    () => subjectOptions.find((o) => o.value === selectedSubjectValue) || null,
    [subjectOptions, selectedSubjectValue],
  );

  // ─── Table state (SERVER-SIDE pagination) ───
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // ── Load academic years for the dropdown ──
  useEffect(() => {
    let mounted = true;

    const loadAcademicYears = async () => {
      if (!schoolId) return;
      setAcademicLoading(true);
      try {
        const res = await AxiosInstance.get(`/school/academic/${schoolId}`);
        const list = res?.data?.data || [];
        if (!mounted) return;

        const previousYears = list.filter(
          (item) => Number(item.isActive) === 0,
        );

        const options = previousYears.map((item) => ({
          label: item.academic_year,
          value: item.id,
        }));
        setAcademicOptions(options);

        const defaultId = previousYears[0]?.id;
        setSelectedAcademicId(defaultId != null ? defaultId : "");
      } catch (err) {
        console.error("Academic year load error:", err);
        if (mounted) {
          setAcademicOptions([]);
          setSelectedAcademicId("");
        }
      } finally {
        if (mounted) setAcademicLoading(false);
      }
    };

    loadAcademicYears();
    return () => {
      mounted = false;
    };
  }, [schoolId]);

  // ── Load Class / Section / Subject options ──
  useEffect(() => {
    let mounted = true;

    const loadClassSectionList = async () => {
      if (!staff_Id || !schoolId) return;
      setSubjectLoading(true);
      try {
        const res = await AxiosInstance.post(
          `/dashboard/staff/getClassSectionList`,
          { staff_id: staff_Id, school_id: schoolId },
        );
        if (!mounted) return;

        const rawList = res?.data?.status ? res.data.data || [] : [];
        setSubjectOptions(buildSubjectOptions(rawList));
      } catch (err) {
        console.error("Class section list load error:", err);
        if (mounted) setSubjectOptions([]);
      } finally {
        if (mounted) setSubjectLoading(false);
      }
    };

    loadClassSectionList();
    return () => {
      mounted = false;
    };
  }, [staff_Id, schoolId]);

  // ── Auto-select the FIRST subject option on initial load ──
  useEffect(() => {
    if (subjectInitRef.current) return;
    if (subjectOptions.length > 0) {
      subjectInitRef.current = true;
      setSelectedSubjectValue(subjectOptions[0].value);
    }
  }, [subjectOptions]);

  // ── Fetch one page of the completed lesson-plan list ──
  const fetchCompletedList = useCallback(async () => {
    const academicId = extractValue(selectedAcademicId);

    if (!schoolId || academicId === "" || academicId == null) {
      setData([]);
      setRowCount(0);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        school_id: schoolId,
        staff_id: staffId,
        academic_id: academicId,
        class_id: selectedSubject?.class_id ?? "",
        class_name: selectedSubject?.class_name || "",
        section_id: selectedSubject?.section_id || "",
        subject_id: selectedSubject?.subject_id ?? "",
        subject_name: selectedSubject?.subject_name || "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      };

      const res = await AxiosInstance.post(
        `/book/lessonPlan/completedList`,
        payload,
      );

      if (res?.data?.status) {
        const resData = res?.data?.data || {};
        const list = resData.list || [];
        const rows = list.map((item, index) => ({
          hm_id: item.hm_id,
          def_id: item.def_id,
          s_no: pagination.pageIndex * pagination.pageSize + index + 1,
          book: item.subject_name || "",
          topic: item.topic || "",
          sub_topic: item.sub_topic || "",
          class_code: item.class_name || "",
          section: item.section_id || "",
          periods: item.period || item.periods || "",
          dayid: item.dayid || item.day || "",
          medium: item.medium || "",
          scheduled_date: item.schedule_date || "",
          completed_date: item.completed_date || "",
          academic_year: item.academic_year || "",
          status: item.status,
          status_text: item.status_label || "",
          created_by: item.staff_name || "",
          approved_by: item.approved_by_name || "",
          _raw: item,
        }));

        setData(rows);
        setRowCount(
          resData.pagination?.total != null
            ? resData.pagination.total
            : resData.total != null
              ? resData.total
              : rows.length,
        );
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (e) {
      console.error("Completed lesson plan list error:", e);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    schoolId,
    staffId,
    selectedAcademicId,
    selectedSubject,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  // Re-fetch whenever academic year OR subject filter OR page changes.
  useEffect(() => {
    fetchCompletedList();
  }, [fetchCompletedList]);

  // Reset to page 0 whenever the academic year OR the subject filter changes
  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [selectedAcademicId, selectedSubjectValue]);

  // 👇 Read the CURRENT selected academic id from the ref (never stale),
  //    and send it to the View screen so it drives the view API.
  const handleView = (row) => {
    navigate("/user/lessonplanview", {
      state: {
        ID: row.def_id,
        hm_id: row.hm_id,
        data: row,
        status: row?.status,
        status_text: row?.status_text,
        fromPreviousYears: true,
        academicId: selectedAcademicIdRef.current,
      },
    });
  };

  const columns = useMemo(() => {
    const cols = buildLessonPlanColumns({
      translations,
      topicOptions: [],
      subTopicOptions: [],
      includeTopic: false,
      includeSubTopic: false,
      includeSection: true,
      includePeriod: false,
      includeDay: false,
      includeScheduledDate: false,
      includeStatus: true,
      approvedStatusCell: false,
      includeCreatedBy: true,
      includeApprovedBy: false,
    });
    cols.push({
      id: "actions",
      header: t.action || "Action",
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <button
          className={styles.viewBtn}
          onClick={() => handleView(row.original)}
          title={t.view || "View"}
        >
          <FaEye />
        </button>
      ),
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translations]);

  return (
    <>
      {/* Gap between the two dropdowns (see design) */}
      <div
        className={styles.toggleRow}
        style={{
          display: "flex",
          gap: "24px",
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 260 }}>
          <CustomDropdown
            label={t.academicYear || "Academic Year"}
            name="academic_year"
            value={selectedAcademicId}
            onChange={(value) =>
              setSelectedAcademicId(extractValue(value) ?? "")
            }
            options={academicOptions}
            placeholder={
              academicLoading
                ? t.loading || "Loading..."
                : t.selectAcademicYear || "Select Academic Year"
            }
          />
        </div>

        {/* Class / Section / Subject filter dropdown */}
        <div style={{ minWidth: 280 }}>
          <CustomDropdown
            label={t.classSectionSubject || "Class / Section / Subject"}
            name="class_section_subject"
            value={selectedSubjectValue}
            onChange={(value) =>
              setSelectedSubjectValue(extractValue(value) || "")
            }
            options={subjectOptions}
            placeholder={
              subjectLoading
                ? t.loading || "Loading..."
                : t.selectClassSectionSubject ||
                  "Select Class / Section / Subject"
            }
            isClearable
          />
        </div>
      </div>

      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        hideGlobalSearch={true}
        enableExpanding={false}
      />
    </>
  );
}

export default PreviousYearsLessonPlan;
